/**
 * Lightweight analytics event dispatcher.
 *
 * Fires a custom event to:
 *  - Google Analytics 4 via the global `gtag` (loaded by AnalyticsLoader when an
 *    admin sets the ga4_id in Admin → Settings).
 *  - `window.dataLayer` (GTM-style buffer) as a fallback so events queue if
 *    gtag hasn't yet loaded by first interaction.
 *  - Plausible via `window.plausible` if it's ever wired up (no-op otherwise).
 *  - Meta (Facebook) Pixel via `window.fbq` — loaded in index.html. Standard
 *    Meta events (`Purchase`, `Lead`, `InitiateCheckout`, `ViewContent`,
 *    `Subscribe`, `CompleteRegistration`, `Contact`, `AddToCart`, `Search`)
 *    fire as `track`; everything else fires as `trackCustom`.
 *
 * Never throws. Safe to call from any component.
 */
const META_STANDARD_EVENTS = new Set([
  "Purchase", "Lead", "InitiateCheckout", "ViewContent", "Subscribe",
  "CompleteRegistration", "Contact", "AddToCart", "Search", "AddPaymentInfo",
  "AddToWishlist", "FindLocation", "Schedule", "StartTrial", "SubmitApplication",
  "Donate", "CustomizeProduct",
]);

export function trackEvent(eventName, params = {}) {
  if (typeof window === "undefined") return;
  try {
    // 1) GA4 via gtag
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
    // 2) dataLayer buffer (queues if gtag not yet defined)
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: eventName, ...params });
    } else {
      window.dataLayer = [{ event: eventName, ...params }];
    }
    // 3) Plausible (custom events) — only fires if Plausible is loaded.
    if (typeof window.plausible === "function") {
      window.plausible(eventName, { props: params });
    }
    // 4) Meta (Facebook) Pixel — standard events use `track`, custom use `trackCustom`.
    if (typeof window.fbq === "function") {
      const fn = META_STANDARD_EVENTS.has(eventName) ? "track" : "trackCustom";
      window.fbq(fn, eventName, params);
    }
  } catch (_e) {
    // Silently swallow — analytics must never break the app.
  }
}
